import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const renderCta = (cta, testId, primary) => {
  if (!cta) return null;
  const cls = primary
    ? "group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider hover:bg-brand-yellowHover transition-all hover:scale-[1.03] glow-yellow"
    : "group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-zinc-300 font-medium uppercase text-xs tracking-[0.2em] hover:text-white transition-all";
  const icon = <ArrowRight size={primary ? 18 : 14} className="group-hover:translate-x-1 transition-transform" />;
  if (cta.href) {
    return (
      <a href={cta.href} target={cta.target || "_blank"} rel="noreferrer" data-testid={testId} className={cls}>
        {cta.label} {icon}
      </a>
    );
  }
  return (
    <Link to={cta.to || "/"} data-testid={testId} className={cls}>
      {cta.label} {icon}
    </Link>
  );
};

export const PageHero = ({
  overline,
  title,
  highlight,
  subtitle,
  description,
  ctaPrimary,
  ctaSecondary,
  bg,
  testId = "page-hero",
}) => {
  return (
    <section
      data-testid={testId}
      className="relative min-h-[78vh] flex items-end overflow-hidden bg-black isolate pt-32 pb-16 sm:pb-20"
    >
      <div className="absolute inset-0 z-0">
        {bg && (
          <img src={bg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45" style={{ filter: "saturate(1.1)" }} />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(90,24,154,0.45),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(255,218,31,0.08),transparent_55%)]" />
        <div className="absolute inset-0 noise opacity-60 pointer-events-none" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 w-full">
        {overline && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-brand-yellow" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.32em] text-brand-yellow">{overline}</span>
          </motion.div>
        )}

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="font-display uppercase text-white leading-[0.9] text-5xl sm:text-7xl lg:text-[7.5rem] tracking-wide max-w-5xl"
        >
          {title} {highlight && <span className="text-brand-yellow text-glow-yellow">{highlight}</span>}
        </motion.h1>

        {subtitle && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }} className="mt-5 font-display text-2xl sm:text-3xl text-brand-purpleLight tracking-wide">
            {subtitle}
          </motion.div>
        )}

        {description && (
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.25 }} className="mt-6 max-w-2xl text-base sm:text-lg text-zinc-300/90 leading-relaxed">
            {description}
          </motion.p>
        )}

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.35 }} className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
          {renderCta(ctaPrimary, "page-hero-cta-primary", true)}
          {renderCta(ctaSecondary, "page-hero-cta-secondary", false)}
        </motion.div>
      </div>
    </section>
  );
};
